import {
  Component,
  OnInit,
  Input,
  HostListener,
  Inject
} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {
  AdobeLaunchService,
  FormFunctionService,
  selectProfile,
  TakataDataService,
  TrackingService
} from '@ox/data';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {AppConfigService} from '@ox/utils';
import {map, take, tap, Subject, takeUntil, fromEvent,filter} from 'rxjs';
import {FORM_ERROR, TakataSignup} from '@ox/typings';
import {RecallsPageService} from '@ox/recalls';
import {AutoDestroy} from '@ox/utils';
import {TakataTrackingData} from '@webu/data';
import {DOCUMENT} from '@angular/common';


@Component({
  selector: 'webu-takata-modal',
  templateUrl: './takata-modal.component.html'
})

export class TakataModalComponent implements OnInit {
  @Input() vehicleMake: string ;
  @Input() vehicleModel: string ;
  @Input() vehicleYear: string ;
  @Input() vin: string;

  takataRecallNums = this.recallsPage.takataEligibleRecallsNumbers; 
  states: {[key: string]: string};
  stateMap = new Map<string, string>();
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();



  ownerDriverRadioOptions = [
    {
      id: 'ownerAndDriverType',
      name: 'userType',
      value: 'B',
      prop10: 'owner and driver',
      label: 'takata_recallSignupFormOwnerDriverRadio'
    },
    {id: 'ownerType', name: 'userType', value: 'O', prop10: 'owner', label: 'takata_recallSignupFormOwnerRadio'},
    {id: 'driverType', name: 'userType', value: 'D', prop10: 'driver', label: 'takata_recallSignupFormDriverRadio'},
    {id: 'otherType', name: 'userType', value: 'N', prop10: 'other', label: 'takata_recallSignupFormOtherRadio'}
  ];

  firstNameAttributes = this.appConfig.fieldAttributes.name.first;
  lastNameAttributes = this.appConfig.fieldAttributes.name.last;
  addressAttributes = this.appConfig.fieldAttributes.address.address;
  cityAttributes = this.appConfig.fieldAttributes.address.city;
  zipAttributes = this.appConfig.fieldAttributes.address.postalCode;
  phoneNumberAttributes = this.appConfig.fieldAttributes.phoneNumber;
  emailAddressAttributes = this.appConfig.fieldAttributes.email;

  mailCheckBox = new FormControl(true);
  emailCheckBox = new FormControl(false);
  voiceCheckBox = new FormControl(false);
  textCheckBox = new FormControl(false);

  customerNameForm: FormGroup = new FormGroup<{ firstName: FormControl, lastName: FormControl }>({
    firstName: new FormControl('', [
      Validators.maxLength(Number(this.firstNameAttributes.maxlength)),
      Validators.minLength(Number(this.firstNameAttributes.minlength)),
      Validators.pattern(this.firstNameAttributes.pattern),
      Validators.required
    ]),
    lastName: new FormControl('', [
      Validators.maxLength(Number(this.lastNameAttributes.maxlength)),
      Validators.minLength(Number(this.firstNameAttributes.minlength)),
      Validators.pattern(this.lastNameAttributes.pattern),
      Validators.required
    ])
  });
  
  customerAddressForm = new FormGroup<{ address1: FormControl, address2: FormControl, city: FormControl, stateName: FormControl, zipCode: FormControl }>({
    address1: new FormControl('', [
      Validators.maxLength(Number(this.addressAttributes.maxlength)),
      Validators.pattern(this.addressAttributes.pattern),
      Validators.required
    ]),
    address2: new FormControl('', [
      Validators.maxLength(Number(this.addressAttributes.maxlength)),
      Validators.pattern(this.addressAttributes.pattern)
    ]),
    city: new FormControl('', [
      Validators.maxLength(+this.cityAttributes.maxlength),
      Validators.pattern(this.cityAttributes.pattern),
      Validators.required
    ]),
    stateName: new FormControl('', Validators.required),
    zipCode: new FormControl('', [
      Validators.maxLength(Number(this.zipAttributes.maxlength)),
      Validators.minLength(Number(this.zipAttributes.minlength)),
      Validators.pattern(this.zipAttributes.pattern),
      Validators.required
    ])
  });
  customerVoicePhoneForm = new FormGroup<{voicePhone: FormControl}>({
    voicePhone: new FormControl('', [
      Validators.maxLength(Number(this.phoneNumberAttributes.maxlength)),
      Validators.min(Number(this.phoneNumberAttributes.minlength)),
      Validators.pattern(this.phoneNumberAttributes.pattern),
      Validators.required
    ])
  });
  customerTextPhoneForm = new FormGroup<{textPhone: FormControl}>({
    textPhone: new FormControl('', [
      Validators.maxLength(Number(this.phoneNumberAttributes.maxlength)),
      Validators.min(Number(this.phoneNumberAttributes.minlength)),
      Validators.pattern(this.phoneNumberAttributes.pattern),
      Validators.required
    ])
  });
  customerEmailForm = new FormGroup<{emailAddress: FormControl}>({
    emailAddress: new FormControl('', [
      Validators.maxLength(Number(this.emailAddressAttributes.maxlength)),
      Validators.minLength(Number(this.emailAddressAttributes.minlength)),
      Validators.pattern(this.emailAddressAttributes.pattern),
      Validators.required
    ])
  });
  ownerAndDriverRadioOptForm = new FormGroup<{ownerDriver: FormControl}>({
    ownerDriver: new FormControl(this.ownerDriverRadioOptions[0].value, [
      Validators.required
    ])
  });

  public selectedState: string;
  profile$ = this.store.select(selectProfile).pipe(
    takeUntil(this.destroy$),
    tap((profile) => {
      if (profile) {
        this.customerNameForm.patchValue({
          firstName: profile.firstName,
          lastName: profile.lastName
        });
        this.customerEmailForm.patchValue({
          emailAddress: profile.emailAddress
        });
        this.customerAddressForm.patchValue({
          address1: profile.address1,
          address2: profile.address2,
          city: profile.city,
          zipCode: profile.postalCode
        });
        this.states$().pipe(take(1)).subscribe({
          next: (states) => {
            this.customerAddressForm.get('stateName')?.setValue(states[profile.stateProvince]); //stateName
            this.updateStateProvince();
          }
        });
        this.fillFormPhoneOptions(profile.phone1Type, profile.phone1);
        this.fillFormPhoneOptions(profile.phone2Type, profile.phone2);
        this.fillFormPhoneOptions(profile.phone3Type, profile.phone3);
      }
    })
  );
  private stateAbbreviation: string | undefined;

  constructor(
    public modalRef: BsModalRef,
    public formFunctionService: FormFunctionService,
    public fb: FormBuilder,
    public appConfig: AppConfigService,
    public takataDataService: TakataDataService,
    public trackingService: TrackingService,
    private store: Store,
    private recallsPage: RecallsPageService,
    public ACDL: AdobeLaunchService,
    @Inject(DOCUMENT)private doc: Document
  ) {
    this.profile$.subscribe();

    const arrayOfForms:FormGroup[] = [this.customerNameForm, this.customerAddressForm, this.customerVoicePhoneForm, this.customerTextPhoneForm, this.customerEmailForm, this.ownerAndDriverRadioOptForm];
    arrayOfForms.forEach((formGroup) => {
      if(formGroup.controls){
        Object.keys(formGroup.controls).forEach((key) => {
          formGroup.controls[key].valueChanges.pipe(
            takeUntil(this.destroy$),
            filter(() => formGroup.controls[key].touched),
            map(() => this.ACDL.handleFormEventFromOX({form:new TakataTrackingData().update(key)}))
          ).subscribe();
        });
      }
    });
  }

  ngOnInit() {
    this.states$().pipe(take(1)).subscribe();
    this.ACDL.handleFormEventFromOX({form: new TakataTrackingData().start()});

  }

  states$() {
    return this.formFunctionService.getStates().pipe(take(1), map((states) => {
      Object.entries(states).forEach(state => this.stateMap.set(state[1], state[0]));
      this.states = states;
      return states;
    }));
  }

  fillFormPhoneOptions(phoneType: string, phone: string) {
    if (phoneType === 'MOBILE') {
      this.customerTextPhoneForm.get('textPhone')?.setValue(this.phoneInitFormat(phone));
    }
    if (phoneType === 'HOME') {
      this.customerVoicePhoneForm.get('voicePhone')?.setValue(this.phoneInitFormat(phone));
    }
  }

  phoneInitFormat(tel: string) {
    if (!tel) {
      return '';
    }
    const value = tel.toString().trim().replace(/^\+/, '');
    if (value.match(/[^0-9]/)) {
      return tel;
    }
    let country: string | number, city: string, number: string;
    switch (value.length) {
      case 10: // +1PPP####### -> C (PPP) ###-####
        country = 1;
        city = value.slice(0, 3);
        number = value.slice(3);
        break;

      case 11: // +CPPP####### -> CCC (PP) ###-####
        country = value[0];
        city = value.slice(1, 4);
        number = value.slice(4);
        break;

      case 12: // +CCCPP####### -> CCC (PP) ###-####
        country = value.slice(0, 3);
        city = value.slice(3, 5);
        number = value.slice(5);
        break;

      default:
        return tel;
    }
    if (country === 1 || country === '1') {
      country = '';
    }
    number = number.slice(0, 3) + '-' + number.slice(3);
    return (country + ' (' + city + ') ' + number).trim();
  }

  saveTakataSignup() {
    const isFormInvalid = this.customerNameForm.invalid || (this.mailCheckBox.value && this.customerAddressForm.invalid) || (this.voiceCheckBox.value && this.customerVoicePhoneForm.invalid) || (this.textCheckBox.value && this.customerTextPhoneForm.invalid) || (this.emailCheckBox.value && this.customerEmailForm.invalid) || (!this.mailCheckBox.value && !this.voiceCheckBox.value && !this.textCheckBox.value && !this.emailCheckBox.value);
    if (!isFormInvalid) {
      const {ownerDriver} = this.ownerAndDriverRadioOptForm.value;
      const signUpObj: TakataSignup = {
        mailOptIn: !!this.mailCheckBox.value,
        phoneOptIn: !!this.voiceCheckBox.value,
        textOptIn: !!this.textCheckBox.value,
        emailOptIn: !!this.emailCheckBox.value,
        firstName: this.customerNameForm?.get('firstName')?.value,
        lastName: this.customerNameForm?.get('lastName')?.value,
        emailAddress: this.customerEmailForm?.get('emailAddress')?.value,
        phoneNumberFormatted: this.formatPhone(this.customerVoicePhoneForm?.get('voicePhone')?.value),
        phoneNumberSmsFormatted: this.formatPhone(this.customerTextPhoneForm?.get('textPhone')?.value),
        phoneNumber: this.customerVoicePhoneForm?.get('voicePhone')?.value,
        phoneNumberSms: this.customerTextPhoneForm?.get('textPhone')?.value,
        userType: ownerDriver,
        vehicleYear: +this.vehicleYear,
        vehicleMake: this.vehicleMake,
        vehicleModel: this.vehicleModel,
        recallNumber: this.takataRecallNums,
        vin: this.vin
      };
      this.takataDataService
        .saveTakataRecallData(signUpObj)
        .pipe(take(1))
        .subscribe((response) => {
          if (response.responseCode === 'Success') {
            this.trackingService.trackEvent('event312', 'recall.takata.saved');
            this.ACDL.handleFormEventFromOX({form: new TakataTrackingData().complete()});
            if(!!this.textCheckBox.value && signUpObj.phoneNumberSmsFormatted){
              this.takataDataService.signUpForSMS(signUpObj).pipe(take(1)).subscribe({
                next: (res) => this.recallsPage.takataSignupMessage = res?.responseCode === 'Success',
                error: () => {
                  this.recallsPage.takataSignupMessage = false;
                  this.doc.dispatchEvent(new CustomEvent(FORM_ERROR, {detail: new TakataTrackingData().error()}));
                }
              });
            }
            else this.recallsPage.takataSignupMessage = true; 
            this.modalRef.hide();
          }
        });
    }
  }

  formatPhone(phone: string) {
    return phone.replace(/[^0-9]/g, '');
  }

  cancelTakataSignup = () => this.closeModal();

  closeModal(){
    this.ACDL.handleFormEventFromOX({form: new TakataTrackingData().cancel()});
    this.modalRef.hide();
  }

  updateStateProvince() {
    const currentState = this.customerAddressForm.get('stateName')?.value;
    if (currentState) {
      this.selectedState = currentState;
      this.stateAbbreviation = this.stateMap.get(currentState);
    }
  }

}
